import React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import DebouncedInput from './DebounceInput';
import Filter, { dateBetweenFilterFn } from './FilterComponent';
import fuzzyFilter from './Filters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import './Table.css';

/**
 * ServerTable
 *
 * Props:
 *  - columns: tanstack columns
 *  - fetchData: async ({ pageIndex, pageSize, sorting, columnFilters, globalFilter }) => { items, totalItems }
 *  - tableClass, containerClass, headerClass, filtersHeaderClass: styling hooks (same as your Table)
 *  - enableGlobalFilter: boolean
 *  - initialPageSize?: number (default 20)
 */
export default function ServerTable({
  columns,
  fetchData,
  tableClass = '',
  containerClass = 'table-responsive',
  headerClass = 'table-light',
  filtersHeaderClass = '',
  enableGlobalFilter = false,
  initialPageSize = 10,
  onRowDoubleClick,
  getRowClassName,
}) {
  const [data, setData] = React.useState([]);
  const [totalItems, setTotalItems] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [sorting, setSorting] = React.useState([]);
  const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: initialPageSize });

  const pageCount = React.useMemo(
    () => Math.max(1, Math.ceil(totalItems / pagination.pageSize)),
    [totalItems, pagination.pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
      columnFilters,
      sorting,
      pagination,
    },
    manualPagination: true,
    pageCount,
    manualSorting: true,
    manualFiltering: true,
    // We still expose the same filterFns so your FilterComponent works,
    filterFns: {
      fuzzy: fuzzyFilter,
      dateBetweenFilterFn,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  // Fetch server data when table state changes
  React.useEffect(() => {
    let ignore = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetchData({
          pageIndex: table.getState().pagination.pageIndex,
          pageSize: table.getState().pagination.pageSize,
          sorting: table.getState().sorting,
          columnFilters: table.getState().columnFilters,
          globalFilter: table.getState().globalFilter,
        });
        if (!ignore) {
          setData(res.items ?? []);
          setTotalItems(res.totalItems ?? 0);
        }
      } catch (err) {
        if (!ignore) setError(err);
        console.error('ServerTable fetch error:', err);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter, columnFilters, sorting, pagination.pageIndex, pagination.pageSize, fetchData]);

  return (
    <>
      <div className={`w-100 ${containerClass}`}>

        {enableGlobalFilter && (
          <div>
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={value => {
                table.setPageIndex(0); // reset to first page on filter
                setGlobalFilter(value);
              }}
              className="p-2 mb-2 form-control form-control-sm"
              placeholder="Search all columns..."
            />
          </div>
        )}

        <div className="h-2" />

        <table className={`table ${tableClass}`}>
          <thead className={`${headerClass}`}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="border-bottom-0 text-nowrap"
                    style={header.column.columnDef?.meta?.headerStyle}
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          className={
                            header.column.getCanSort()
                              ? 'cursor-pointer select-none py-2 px-1'
                              : 'py-2 px-1'
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <FontAwesomeIcon className="ms-2 text-accent2" icon={faSortUp} />,
                            desc: <FontAwesomeIcon className="ms-2 text-accent2" icon={faSortDown} />,
                          }[header.column.getIsSorted()] ??
                            (header.column.getCanSort() ? (
                              <FontAwesomeIcon className="ms-2 text-accent2" icon={faSort} />
                            ) : null)}
                        </div>
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <thead className={`${filtersHeaderClass}`}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={header.column.columnDef?.meta?.filterStyle}
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter
                              column={header.column}
                              table={table}
                              // Whenever a filter changes, jump to first page
                              onFilterApplied={() => table.setPageIndex(0)}
                            />
                          </div>
                        ) : null}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className="table-group-divider">
            {isLoading && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="py-4 text-center text-muted">Loading…</div>
                </td>
              </tr>
            )}
            {!isLoading && data.length === 0 && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="py-4 text-center text-muted">No records found</div>
                </td>
              </tr>
            )}
            {!isLoading &&
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className={getRowClassName ? getRowClassName(row.original, row) : ''}
                  onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row.original, row) : undefined}
                  style={onRowDoubleClick ? { cursor: 'pointer' } : undefined}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} style={cell.column.columnDef?.meta?.cellStyle}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex align-items-center gx-2 mt-3">
        <nav>
          <ul className={`pagination m-0 ${tableClass.includes('table-sm') ? 'pagination-sm' : ''}`}>
            <li className={`page-item ${pagination.pageIndex === 0 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => table.setPageIndex(0)} aria-label="First">
                «
              </button>
            </li>
            <li className={`page-item ${pagination.pageIndex === 0 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => table.previousPage()} aria-label="Previous">
                &lt;
              </button>
            </li>
            <li className={`page-item ${pagination.pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => table.nextPage()} aria-label="Next">
                &gt;
              </button>
            </li>
            <li className={`page-item ${pagination.pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => table.setPageIndex(pageCount - 1)} aria-label="Last">
                »
              </button>
            </li>
          </ul>
        </nav>

        <span className="d-flex align-items-center gx-1 ms-2">
          <div>Page</div>
          <strong className="ms-1">
            {pagination.pageIndex + 1} of {pageCount}
          </strong>
        </span>

        <span className="d-flex align-items-center gx-1 ms-2">
          | Go to page:
          <input
            type="number"
            min={1}
            value={pagination.pageIndex + 1}
            onChange={e => {
              const p = e.target.value ? Number(e.target.value) - 1 : 0;
              table.setPageIndex(Number.isNaN(p) ? 0 : p);
            }}
            className={`form-control w-auto ms-1 ${tableClass.includes('table-sm') ? 'form-control-sm' : ''}`}
          />
        </span>

        <select
          value={pagination.pageSize}
          onChange={e => table.setPageSize(Number(e.target.value))}
          className={`form-select w-auto ms-2 ${tableClass.includes('table-sm') ? 'form-select-sm' : ''}`}
        >
          {[10, 20, 30, 40, 50, 100].map(ps => (
            <option key={ps} value={ps}>
              Show {ps}
            </option>
          ))}
        </select>

        {error && <span className="ms-3 text-danger small">Failed to load data.</span>}
      </div>
    </>
  );
}
